import React from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

function formatDateTime(value) {
  if (!value) return "—";
  const dt = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString();
}

function getFileExtension(fileName) {
  const name = String(fileName || "");
  const parts = name.split(".");
  if (parts.length < 2) return null;
  return parts[parts.length - 1].toUpperCase();
}

export default function DataCenterHub() {
  const theme = useTheme();

  // Placeholder data until backend endpoints are connected.
  const lastPlatformDataUpdate = "2024-12-31T00:00:00.000Z";

  const uploadHistory = [
    {
      id: "upl_001",
      fileName: "sales_data_dec_2024.xlsx",
      uploadedBy: "Admin",
      uploadedAt: "2025-01-02T10:12:00.000Z",
      rows: 128450,
    },
    {
      id: "upl_002",
      fileName: "promo_calendar_q1.csv",
      uploadedBy: "Analyst",
      uploadedAt: "2025-01-05T09:31:00.000Z",
      rows: 2200,
    },
    {
      id: "upl_003",
      fileName: "assortment_master.csv",
      uploadedBy: "Analyst",
      uploadedAt: "2025-01-08T15:45:00.000Z",
      rows: 0,
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={2}>
        <Paper
          sx={{
            p: 2.25,
            borderRadius: 3,
            color: "common.white",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0B3A82 100%)`,
            position: "relative",
            overflow: "hidden",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at 85% 25%, ${alpha("#FFFFFF", 0.18)} 0%, transparent 55%)`,
            },
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Typography variant="h5" fontWeight={800}>
            Data Center Hub
          </Typography>
            <Typography sx={{ mt: 0.5, opacity: 0.9 }}>
              Reference page for data updates and import history.
          </Typography>
          </Box>
        </Paper>

        <Paper
          sx={{
            p: 2.25,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
              theme.palette.primary.main,
              0.0
            )} 100%)`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: theme.palette.primary.main }}>
            <HistoryOutlinedIcon fontSize="small" />
            <Typography fontWeight={800} color="text.primary">
              Last platform data update
            </Typography>
          </Stack>
          <Typography sx={{ mt: 1 }} fontSize={22} fontWeight={800}>
            {formatDateTime(lastPlatformDataUpdate)}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.25 }}>
            The timestamp of the latest available platform data.
          </Typography>
        </Paper>

        <Paper sx={{ borderRadius: 3, p: 2.25 }}>
          <Typography sx={{ mt: 2 }} fontWeight={800}>
            Upload history
          </Typography>
          <TableContainer
            sx={{
              mt: 1,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                    }}
                  >
                    File
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    Uploaded by
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    Uploaded at
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 800,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                    }}
                  >
                    Rows
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadHistory.map((u) => (
                  <TableRow
                    key={u.id}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          variant="outlined"
                          label={getFileExtension(u.fileName) ?? "FILE"}
                          sx={{
                            fontWeight: 800,
                            borderColor: alpha(theme.palette.primary.main, 0.35),
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          }}
                        />
                        <Typography fontWeight={700}>{u.fileName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{u.uploadedBy}</TableCell>
                    <TableCell>{formatDateTime(u.uploadedAt)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 700,
                      }}
                    >
                      {u.rows?.toLocaleString?.() ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
}

